/**
 * Bar-aligned Trade Review overlays (same length/order as `candles[]`).
 * Opt-in via `include_overlays` / legacy `include_regime_overlay`.
 * `null` / `"unknown"` = do not paint.
 */

export type ExecutionOverlayKind =
  | "regime"
  | "direction"
  | "direction_short"
  | "session"
  | "sma"
  | "bb"
  | "adx"
  | "rsi"
  | "atr"
  | "sr"
  | "adx_bucket"
  | "rsi_bucket"
  | "atr_regime"
  | "squeeze"
  | "bb_zone"
  | "bb_quartile";

/** Categorical label series (regime / direction / session / buckets). */
export type ExecutionCandlesLabelOverlay = {
  candle_timeframe?: string;
  classifier_version?: string;
  conditions: Array<string | null>;
  message?: string;
};

/** Price-pane numeric series (SMA / Bollinger). Leading nulls = warmup. */
export type ExecutionCandlesPriceOverlayPoint = {
  time?: string | null;
  /** Legacy fixed keys — also covered by StrategyRdMaSeriesKey. */
  sma50?: number | null;
  sma200?: number | null;
  ema10?: number | null;
  ema20?: number | null;
  bb_upper?: number | null;
  bb_middle?: number | null;
  bb_lower?: number | null;
  /** Dynamic SMA/EMA lengths used by Strategy Lab (sma5, ema21, …). */
  [key: string]: string | number | null | undefined;
};

export type ExecutionCandlesPriceOverlay = {
  candle_timeframe?: string;
  series: ExecutionCandlesPriceOverlayPoint[];
  message?: string;
};

/** Oscillator pane series. */
export type ExecutionCandlesPaneOverlayPoint = {
  time?: string | null;
  adx?: number | null;
  plus_di?: number | null;
  minus_di?: number | null;
  rsi?: number | null;
  atr?: number | null;
  atr_pct?: number | null;
  /** MACD (FE-local or BE). */
  macd?: number | null;
  macd_signal?: number | null;
  macd_hist?: number | null;
  /** Stochastic %K / %D. */
  stoch_k?: number | null;
  stoch_d?: number | null;
  /** Money Flow Index. */
  mfi?: number | null;
};

export type ExecutionCandlesPaneOverlay = {
  candle_timeframe?: string;
  series: ExecutionCandlesPaneOverlayPoint[];
  message?: string;
};

/** Focused-trade S/R snapshot (not a full window series). */
export type ExecutionCandlesSrSnapshot = {
  nearest_support?: number | null;
  nearest_resistance?: number | null;
  room_bucket?: string | null;
  message?: string;
};

/**
 * Bundle of optional overlays on GET /execution-candles.
 * `regime_overlay` remains the legacy field; also mirrored here when present.
 */
export type ExecutionCandlesOverlaysBundle = {
  regime?: ExecutionCandlesLabelOverlay | null;
  direction?: ExecutionCandlesLabelOverlay | null;
  direction_short?: ExecutionCandlesLabelOverlay | null;
  session?: ExecutionCandlesLabelOverlay | null;
  adx_bucket?: ExecutionCandlesLabelOverlay | null;
  rsi_bucket?: ExecutionCandlesLabelOverlay | null;
  atr_regime?: ExecutionCandlesLabelOverlay | null;
  squeeze?: ExecutionCandlesLabelOverlay | null;
  bb_zone?: ExecutionCandlesLabelOverlay | null;
  bb_quartile?: ExecutionCandlesLabelOverlay | null;
  price?: ExecutionCandlesPriceOverlay | null;
  panes?: ExecutionCandlesPaneOverlay | null;
  sr?: ExecutionCandlesSrSnapshot | null;
};

/** Tessera Lane B overlay ids the UI can toggle / auto-enable from picks. */
export type TesseraLaneBOverlayId =
  | "regime"
  | "direction"
  | "direction_short"
  | "session"
  | "sma5"
  | "sma8"
  | "sma9"
  | "sma10"
  | "sma20"
  | "sma21"
  | "sma30"
  | "sma50"
  | "sma100"
  | "sma200"
  | "ema5"
  | "ema8"
  | "ema9"
  | "ema10"
  | "ema12"
  | "ema20"
  | "ema21"
  | "ema50"
  | "ema100"
  | "ema200"
  | "bb"
  | "adx"
  | "rsi"
  | "atr"
  | "macd"
  | "stoch"
  | "mfi"
  | "adx_bucket"
  | "rsi_bucket"
  | "atr_regime"
  | "squeeze"
  | "bb_zone"
  | "bb_quartile"
  /** Strategy Lab: BE indicator-confluence series over price / in sub-panels (§1.6c). */
  | "rd_price"
  | "rd_sub_0"
  | "rd_sub_1"
  | "rd_sub_2"
  | "rd_sub_3"
  | "sr";

export const TESSERA_LANE_B_OVERLAY_IDS: readonly TesseraLaneBOverlayId[] = [
  "regime",
  "direction",
  "direction_short",
  "session",
  "sma5",
  "sma8",
  "sma9",
  "sma10",
  "sma20",
  "sma21",
  "sma30",
  "sma50",
  "sma100",
  "sma200",
  "ema5",
  "ema8",
  "ema9",
  "ema10",
  "ema12",
  "ema20",
  "ema21",
  "ema50",
  "ema100",
  "ema200",
  "bb",
  "adx",
  "rsi",
  "atr",
  "macd",
  "stoch",
  "mfi",
  "adx_bucket",
  "rsi_bucket",
  "atr_regime",
  "squeeze",
  "bb_zone",
  "bb_quartile",
  "rd_price",
  "rd_sub_0",
  "rd_sub_1",
  "rd_sub_2",
  "rd_sub_3",
  "sr",
];

export function parseIncludeOverlaysParam(
  raw: string | null | undefined
): ExecutionOverlayKind[] {
  if (!raw?.trim()) return [];
  const allowed = new Set<string>([
    "regime",
    "direction",
    "direction_short",
    "session",
    "sma",
    "bb",
    "adx",
    "rsi",
    "atr",
    "sr",
    "adx_bucket",
    "rsi_bucket",
    "atr_regime",
    "squeeze",
    "bb_zone",
    "bb_quartile",
  ]);
  const out: ExecutionOverlayKind[] = [];
  for (const part of raw.split(",")) {
    const key = part.trim().toLowerCase();
    if (allowed.has(key) && !out.includes(key as ExecutionOverlayKind)) {
      out.push(key as ExecutionOverlayKind);
    }
  }
  return out;
}

/** Map Lane B UI ids → API `include_overlays` tokens. */
export function laneBIdsToIncludeOverlays(
  ids: readonly TesseraLaneBOverlayId[]
): ExecutionOverlayKind[] {
  const set = new Set<ExecutionOverlayKind>();
  for (const id of ids) {
    switch (id) {
      case "regime":
        set.add("regime");
        break;
      case "direction":
        set.add("direction");
        break;
      case "direction_short":
        set.add("direction_short");
        break;
      case "session":
        set.add("session");
        break;
      case "sma5":
      case "sma8":
      case "sma9":
      case "sma10":
      case "sma20":
      case "sma21":
      case "sma30":
      case "sma50":
      case "sma100":
      case "sma200":
        set.add("sma");
        break;
      case "ema5":
      case "ema8":
      case "ema9":
      case "ema10":
      case "ema12":
      case "ema20":
      case "ema21":
      case "ema50":
      case "ema100":
      case "ema200":
        // Lab / client-computed — execution-candles has no EMA overlay token.
        break;
      case "bb":
        set.add("bb");
        break;
      case "adx":
        set.add("adx");
        break;
      case "rsi":
        set.add("rsi");
        break;
      case "atr":
        set.add("atr");
        break;
      case "adx_bucket":
        set.add("adx_bucket");
        break;
      case "rsi_bucket":
        set.add("rsi_bucket");
        break;
      case "atr_regime":
        set.add("atr_regime");
        break;
      case "squeeze":
        set.add("squeeze");
        break;
      case "bb_zone":
        set.add("bb_zone");
        break;
      case "bb_quartile":
        set.add("bb_quartile");
        break;
      case "sr":
        set.add("sr");
        break;
      default:
        break;
    }
  }
  return Array.from(set).sort();
}
